import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import NangoConnectionsApi from '@/infrastructure/api/nango-connections'
import { NangoConnection } from '@/domain/nango-connection'

const NANGO_CONNECTIONS_KEY = ['nangoConnections']

export function nangoConnectionsKey(ecommerceId?: string) {
  return [...NANGO_CONNECTIONS_KEY, ecommerceId]
}

// Its own root: a group is often named like one of its stores, so the store keys would collide.
const GROUP_NANGO_CONNECTIONS_KEY = ['groupNangoConnections']

export function groupNangoConnectionsKey(group?: string) {
  return [...GROUP_NANGO_CONNECTIONS_KEY, group]
}

// The store travels with the request: hook options change on rerender, a request's variables do not.
type StoreConnectionRef = Pick<
  NangoConnection,
  'integration' | 'connectionId'
> & {
  ecommerceId: string
}

export function useNangoConnections({
  enabled,
  ecommerceId
}: {
  enabled: boolean
  ecommerceId?: string
}) {
  const query = useQuery({
    queryKey: nangoConnectionsKey(ecommerceId),
    queryFn: ({ signal }) =>
      NangoConnectionsApi.listConnections(ecommerceId ?? '', signal),
    enabled: enabled && !!ecommerceId
  })

  return {
    connections: query.data ?? [],
    loadingConnections: query.isLoading,
    connectionsError: query.isError,
    reloadConnections: query.refetch
  }
}

export function useGroupNangoConnections({
  enabled,
  group,
  retry
}: {
  enabled: boolean
  group?: string
  /** Retries after a failure; React Query's default (3) when left out. */
  retry?: number
}) {
  const query = useQuery({
    queryKey: groupNangoConnectionsKey(group),
    queryFn: ({ signal }) =>
      NangoConnectionsApi.listGroupConnections(group ?? '', signal),
    enabled: enabled && !!group,
    retry
  })

  return {
    connections: query.data ?? [],
    loadingConnections: query.isLoading,
    connectionsError: query.isError,
    reloadConnections: query.refetch
  }
}

// Not a mutation: React Query would hold the token in its cache while mounted.
export function createNangoConnectSession(
  group: string,
  allowedIntegrations?: string[]
) {
  return NangoConnectionsApi.createConnectSession(group, allowedIntegrations)
}

export function createNangoReconnectSession(
  group: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  return NangoConnectionsApi.createReconnectSession(group, connection)
}

export function checkNangoConnection(
  group: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  return NangoConnectionsApi.checkConnection(group, connection)
}

export function useLinkNangoConnection() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ ecommerceId, ...connection }: StoreConnectionRef) =>
      NangoConnectionsApi.linkConnection(ecommerceId, connection),
    onSuccess: (_, { ecommerceId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: nangoConnectionsKey(ecommerceId)
        }),
        // The group lists show each connection's stores.
        queryClient.invalidateQueries({ queryKey: GROUP_NANGO_CONNECTIONS_KEY })
      ])
  })

  return {
    linkConnection: mutation.mutateAsync,
    linking: mutation.isPending
  }
}

export function useUnlinkNangoConnection() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ ecommerceId, ...connection }: StoreConnectionRef) =>
      NangoConnectionsApi.unlinkConnection(ecommerceId, connection),
    onSuccess: (_, { ecommerceId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: nangoConnectionsKey(ecommerceId)
        }),
        // The group lists show each connection's stores.
        queryClient.invalidateQueries({ queryKey: GROUP_NANGO_CONNECTIONS_KEY })
      ])
  })

  return {
    unlinkConnection: mutation.mutateAsync,
    unlinking: mutation.isPending
  }
}

export function useAssignNangoPlatformConnection() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      ecommerceId,
      switchStore,
      ...connection
    }: StoreConnectionRef & { switchStore?: boolean }) =>
      NangoConnectionsApi.assignPlatformConnection(
        ecommerceId,
        connection,
        switchStore
      ),
    onSuccess: (_, { ecommerceId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: nangoConnectionsKey(ecommerceId)
        }),
        // The group lists show each connection's stores.
        queryClient.invalidateQueries({ queryKey: GROUP_NANGO_CONNECTIONS_KEY })
      ])
  })

  return {
    assignPlatformConnection: mutation.mutateAsync,
    assigning: mutation.isPending
  }
}

type GroupConnectionRef = Pick<
  NangoConnection,
  'integration' | 'connectionId'
> & {
  group: string
}

export function useDeleteNangoConnection() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ group, ...connection }: GroupConnectionRef) =>
      NangoConnectionsApi.deleteConnection(group, connection),
    // Settled, not success: when Nango fails, the server has already unassigned the stores.
    onSettled: (_, __, { group }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: groupNangoConnectionsKey(group)
        }),
        // Deleting unassigns it from every store, whose lists hold it too.
        queryClient.invalidateQueries({ queryKey: NANGO_CONNECTIONS_KEY })
      ])
  })

  return {
    deleteConnection: mutation.mutateAsync,
    deleting: mutation.isPending
  }
}
